import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon } from './Icons';
import Pagination from './Pagination';
import CallButton from './CallButton';
import { getKnownAgents, normalizeNumber, getCustomerNumber, getCallStatus, localDateStr } from './callUtils';

const DEFAULT_PAGE_SIZE = 50;
// Orders qualify/drop off this list as their status changes throughout the
// day - poll so agents see that without a manual refresh, same reasoning as
// App.js's 60s calls-list fallback poll and IncomingCallPopup.js's 8s
// missed-SSE poll.
const AUTO_REFRESH_MS = 10000;

// FIFO: oldest order first, so agents work through the backlog in the order
// customers placed it in rather than whatever order the ERP happens to
// return. Orders with an unparseable/missing order_date sort to the end -
// there's no placement to infer for them, but they shouldn't jump the queue.
const sortOrdersFifo = (list) =>
  [...list].sort((a, b) => {
    const aTime = a.order_date ? new Date(a.order_date).getTime() : NaN;
    const bTime = b.order_date ? new Date(b.order_date).getTime() : NaN;
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;
    return aTime - bTime;
  });

// "Today" when actionLogDate is today, else a short "22 Aug" label - shown
// on the Confirmed/Rejected stat cards so the count on the card is never
// mistaken for an all-time total once a different date is picked. Parsed as
// a local (not UTC) date, same reasoning as DateRangePicker's parseLocalDate.
const formatActionCardDate = (dateStr) => {
  if (dateStr === localDateStr(new Date())) return 'Today';
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

// A call list of every ERP order still awaiting a COD-confirmation call
// (financially pending and unfulfilled - see the ERP's DailyOrdersView),
// not scoped to any date, so agents don't have to re-key anything from the
// ERP by hand. Always a live fetch — see server/ivr/erpClient.js's
// fetchDailyOrders — nothing from the ERP is ever stored here, same
// principle as the incoming-call popup (IncomingCallPopup.js).
function DailyOrders({ calls }) {
  const { authFetch } = useAuth();
  const knownAgents = getKnownAgents(calls);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Defaults to "Not Called Yet" - that's the actual work list agents land
  // on, not the full mixed bag of everything already in progress.
  const [callFilter, setCallFilter] = useState('NotCalled');
  // Per-order Confirm/Cancel state, keyed by order_number: 'working' while
  // the request is in flight, or { error } if the ERP rejected it. Success
  // isn't tracked here - the order is just removed from `orders` instead.
  const [actionState, setActionState] = useState({});
  // Tally of Confirm/Cancel clicks on actionLogDate (today by default), for
  // the two stat cards below. Confirmed/cancelled orders drop out of
  // `orders` as soon as the ERP accepts the tag, so there's nothing left in
  // `orders` afterward to count from - this is backed by the server's
  // ivr_cod_actions table (see server/ivr/db.js) precisely so it survives
  // page reloads and is shared across every agent's browser, not just this
  // tab's session.
  const [actionCounts, setActionCounts] = useState({ confirmed: 0, cancelled: 0 });
  // The individual rows behind whichever of the two counts above was last
  // clicked into (order_number/agent/created_at only - see
  // getCodActionsByType in server/ivr/db.js). Fetched on demand rather than
  // alongside the 10s counts poll, since the full list is only needed while
  // an agent is actually looking at the Confirmed/Rejected card.
  const [actionLog, setActionLog] = useState([]);
  const [actionLogLoading, setActionLogLoading] = useState(false);
  const [actionLogError, setActionLogError] = useState('');
  // Confirmed/Rejected default to today's actions only (agents mainly care
  // "what did we resolve today"), with this date picker to look back at any
  // other day - see the matching `date` param on GET
  // /erp/daily-orders/actions and getCodActionsByType in server/ivr/db.js.
  const [actionLogDate, setActionLogDate] = useState(() => localDateStr(new Date()));

  // `silent` skips the full-page loading state for the background
  // auto-refresh poll, so the table doesn't flicker to a spinner every 10s -
  // only the initial load and manual Refresh clicks show it.
  const fetchOrders = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await authFetch('/api/ivr/erp/daily-orders');
      const body = await response.json();
      if (!response.ok) {
        if (!silent) setOrders([]);
        setError(body?.error || 'Failed to fetch orders from the ERP.');
        return;
      }
      setError('');
      setOrders(sortOrdersFifo(Array.isArray(body?.orders) ? body.orders : []));
    } catch (err) {
      if (!silent) setOrders([]);
      setError(err.message || 'Failed to fetch orders from the ERP.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchActionCounts = async (date) => {
    try {
      const response = await authFetch(
        `/api/ivr/erp/daily-orders/action-counts?date=${encodeURIComponent(date)}`
      );
      const body = await response.json();
      if (response.ok) {
        setActionCounts({ confirmed: body?.confirmed || 0, cancelled: body?.cancelled || 0 });
      }
    } catch (err) {
      // Non-critical - the stat cards just keep showing the last known counts.
    }
  };

  const fetchActionLog = async (type, date) => {
    setActionLogLoading(true);
    setActionLogError('');
    try {
      const response = await authFetch(
        `/api/ivr/erp/daily-orders/actions?type=${type}&date=${encodeURIComponent(date)}`
      );
      const body = await response.json();
      if (!response.ok) {
        setActionLog([]);
        setActionLogError(body?.error || 'Failed to fetch this list.');
        return;
      }
      setActionLog(Array.isArray(body?.actions) ? body.actions : []);
    } catch (err) {
      setActionLog([]);
      setActionLogError(err.message || 'Failed to fetch this list.');
    } finally {
      setActionLogLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(() => fetchOrders({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  // The Confirmed/Rejected card numbers are scoped to actionLogDate (today
  // by default) rather than polled once for an all-time total, so the badge
  // always matches whichever day's list the date picker below is showing -
  // re-run whenever that date changes, not just on mount.
  useEffect(() => {
    fetchActionCounts(actionLogDate);
    const interval = setInterval(() => fetchActionCounts(actionLogDate), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [actionLogDate]);

  useEffect(() => {
    setPage(1);
  }, [orders.length, callFilter]);

  // Confirmed/Rejected are backed by a different data source (the local
  // action log, not the ERP's live orders list) - fetch it the moment either
  // card is selected or the date picker changes, same "fetch on demand"
  // reasoning as fetchActionLog itself.
  useEffect(() => {
    if (callFilter === 'Confirmed') fetchActionLog('confirm', actionLogDate);
    else if (callFilter === 'Rejected') fetchActionLog('cancel', actionLogDate);
  }, [callFilter, actionLogDate]);

  // Defaults back to today every time a Confirmed/Rejected card is clicked
  // (not just on first load), so switching tabs and back doesn't leave an
  // agent stuck looking at a stale picked date without noticing.
  const selectActionFilter = (filter) => {
    setActionLogDate(localDateStr(new Date()));
    setCallFilter(filter);
  };

  // How many times this order's customer has been rung via click-to-call
  // (the COD-confirmation calls agents place from this page), and whether
  // any of those attempts got picked up - drives the stat cards and filter
  // below. Scoped to calls made after this order's own order_date, not just
  // "ever called this number" - otherwise a customer's older, unrelated
  // answered call (from a previous order) would mark a brand-new order as
  // already resolved before anyone touched it.
  const getOrderCallBucket = (order) => {
    const customerNumber = normalizeNumber(order.mobile_number);
    const orderPlacedAt = order.order_date ? new Date(order.order_date).getTime() : NaN;
    const attempts = calls.filter((call) => {
      if (!(call.direction || '').toLowerCase().includes('click')) return false;
      if (getCustomerNumber(call) !== customerNumber) return false;
      if (Number.isNaN(orderPlacedAt)) return true;
      const calledAt = new Date((call.start_stamp || '').replace(' ', 'T')).getTime();
      return Number.isNaN(calledAt) || calledAt >= orderPlacedAt;
    });
    if (attempts.some((call) => getCallStatus(call) === 'Answered')) return 'Answered';
    if (attempts.length >= 2) return 'Attempted2';
    if (attempts.length === 1) return 'Attempted1';
    return 'NotCalled';
  };

  const orderBuckets = orders.map((order) => getOrderCallBucket(order));
  const bucketCounts = {
    NotCalled: orderBuckets.filter((bucket) => bucket === 'NotCalled').length,
    Attempted1: orderBuckets.filter((bucket) => bucket === 'Attempted1').length,
    Attempted2: orderBuckets.filter((bucket) => bucket === 'Attempted2').length,
    Answered: orderBuckets.filter((bucket) => bucket === 'Answered').length,
  };

  // Confirmed/Rejected show the local action log instead of the ERP's live
  // orders list (see the fetchActionLog effect above) - `orders` has nothing
  // left to filter once an order's been actioned, since the ERP drops it
  // from its pending list the moment the tag is accepted.
  const isActionView = callFilter === 'Confirmed' || callFilter === 'Rejected';

  const filteredOrders =
    callFilter === 'All' || isActionView
      ? orders
      : orders.filter((order, index) => orderBuckets[index] === callFilter);

  const activeRows = isActionView ? actionLog : filteredOrders;
  const totalPages = Math.max(1, Math.ceil(activeRows.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = activeRows.slice((page - 1) * pageSize, page * pageSize);

  // Names and quantities render as separate columns (one line per product,
  // in the same order in both columns) instead of one combined "Name x1, ..."
  // string, so a multi-product order's quantities are easy to read/fetch at
  // a glance instead of hunting for "x1" inside a wrapped sentence.
  const renderProductNames = (products) =>
    (products || []).length ? products.map((p, i) => <div key={i}>{p.name}</div>) : '-';

  const renderProductQtys = (products) =>
    (products || []).length ? products.map((p, i) => <div key={i}>{p.qty ?? '-'}</div>) : '-';

  const formatPrice = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) return '-';
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  const formatOrderDate = (orderDate) => {
    if (!orderDate) return '-';
    const parsed = new Date(orderDate);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Tags the order in Shopify (via the ERP) as resolved by phone - see
  // server/ivr/routes.js's /erp/daily-orders/:orderNumber/(confirm|cancel).
  // Fail closed: the order only disappears from this list on a 200 from the
  // ERP, never optimistically, since a failure means Shopify was never
  // actually updated.
  const handleCodAction = async (orderNumber, action) => {
    setActionState((prev) => ({ ...prev, [orderNumber]: { status: 'working' } }));
    try {
      const response = await authFetch(
        `/api/ivr/erp/daily-orders/${encodeURIComponent(orderNumber)}/${action}`,
        { method: 'POST' }
      );
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || `Failed to ${action} order`);
      setOrders((prev) => prev.filter((order) => order.order_number !== orderNumber));
      const key = action === 'confirm' ? 'confirmed' : 'cancelled';
      setActionCounts((prev) => ({ ...prev, [key]: prev[key] + 1 }));
    } catch (error) {
      setActionState((prev) => ({
        ...prev,
        [orderNumber]: { status: 'error', message: error.message },
      }));
    }
  };

  return (
    <>
      <div className="incoming-filters-bar">
        <button type="button" className="btn btn-secondary" onClick={() => fetchOrders()} disabled={loading}>
          <RefreshIcon width={14} height={14} />
          Refresh
        </button>
      </div>

      <div className="stat-cards">
        <button
          type="button"
          className={`stat-card ${callFilter === 'All' ? 'active' : ''}`}
          onClick={() => setCallFilter('All')}
        >
          <div className="stat-value">{orders.length}</div>
          <div className="stat-label">Total Orders</div>
        </button>
        <button
          type="button"
          className={`stat-card ${callFilter === 'NotCalled' ? 'active' : ''}`}
          onClick={() => setCallFilter('NotCalled')}
        >
          <div className="stat-value">{bucketCounts.NotCalled}</div>
          <div className="stat-label">Pending Confirmation</div>
        </button>
        <button
          type="button"
          className={`stat-card attempted ${callFilter === 'Attempted1' ? 'active' : ''}`}
          onClick={() => setCallFilter('Attempted1')}
        >
          <div className="stat-value">{bucketCounts.Attempted1}</div>
          <div className="stat-label">Keep Try Again</div>
        </button>
        <button
          type="button"
          className={`stat-card critical ${callFilter === 'Attempted2' ? 'active' : ''}`}
          onClick={() => setCallFilter('Attempted2')}
        >
          <div className="stat-value">{bucketCounts.Attempted2}</div>
          <div className="stat-label">Not Received</div>
        </button>
        <button
          type="button"
          className={`stat-card answered ${callFilter === 'Answered' ? 'active' : ''}`}
          onClick={() => setCallFilter('Answered')}
        >
          <div className="stat-value">{bucketCounts.Answered}</div>
          <div className="stat-label">Answered</div>
        </button>
        <button
          type="button"
          className={`stat-card answered ${callFilter === 'Confirmed' ? 'active' : ''}`}
          title={`Orders confirmed as COD on ${formatActionCardDate(actionLogDate)} - click to see the list`}
          onClick={() => selectActionFilter('Confirmed')}
        >
          <div className="stat-value">{actionCounts.confirmed}</div>
          <div className="stat-label">
            Confirmed <span className="stat-label-date">({formatActionCardDate(actionLogDate)})</span>
          </div>
        </button>
        <button
          type="button"
          className={`stat-card critical ${callFilter === 'Rejected' ? 'active' : ''}`}
          title={`Orders cancelled on ${formatActionCardDate(actionLogDate)} - click to see the list`}
          onClick={() => selectActionFilter('Rejected')}
        >
          <div className="stat-value">{actionCounts.cancelled}</div>
          <div className="stat-label">
            Rejected <span className="stat-label-date">({formatActionCardDate(actionLogDate)})</span>
          </div>
        </button>
      </div>

      {isActionView && (
        <div className="incoming-filters-bar">
          <label htmlFor="action-log-date">
            {callFilter === 'Confirmed' ? 'Confirmed on' : 'Rejected on'}
          </label>
          <input
            id="action-log-date"
            type="date"
            className="date-filter-input"
            value={actionLogDate}
            max={localDateStr(new Date())}
            onChange={(e) => setActionLogDate(e.target.value)}
          />
        </div>
      )}

      {isActionView
        ? actionLogError && <div className="call-feedback call-feedback-error">{actionLogError}</div>
        : error && <div className="call-feedback call-feedback-error">{error}</div>}

      {isActionView ? (
        actionLogLoading ? (
          <div className="loading-state">Loading…</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th>Agent</th>
                  <th>Date/Time</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center' }}>
                      {actionLogError
                        ? 'Could not load this list.'
                        : `No orders ${callFilter === 'Confirmed' ? 'confirmed' : 'rejected'} on ${actionLogDate}.`}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, index) => (
                    <tr key={`${row.order_number}-${row.created_at}-${index}`}>
                      <td className="table-cell-strong">{row.order_number || '-'}</td>
                      <td>{row.agent || '-'}</td>
                      <td>{row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )
      ) : loading ? (
        <div className="loading-state">Loading…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order Number</th>
                <th>Order Date/Time</th>
                <th>Customer Name</th>
                <th>Mobile Number</th>
                <th>Products</th>
                <th>Qty</th>
                <th>Total Price</th>
                <th>Call</th>
                <th>COD Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center' }}>
                    {error
                      ? 'Could not load orders from the ERP.'
                      : orders.length === 0
                      ? 'No orders currently need a confirmation call.'
                      : 'No orders match this filter.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((order) => {
                  const rowAction = actionState[order.order_number] || {};
                  const working = rowAction.status === 'working';
                  const hasCalled = getOrderCallBucket(order) !== 'NotCalled';
                  return (
                    <tr key={order.order_number || `${order.mobile_number}-${order.name}`}>
                      <td className="table-cell-strong">{order.order_number || '-'}</td>
                      <td>{formatOrderDate(order.order_date)}</td>
                      <td>{order.name || '-'}</td>
                      <td>{order.mobile_number || '-'}</td>
                      <td>{renderProductNames(order.products)}</td>
                      <td>{renderProductQtys(order.products)}</td>
                      <td>{formatPrice(order.total_price)}</td>
                      <td>
                        <CallButton
                          agents={knownAgents}
                          customerNumber={normalizeNumber(order.mobile_number)}
                          onCallTriggered={() => {}}
                        />
                      </td>
                      <td>
                        <div className="cod-actions">
                          <button
                            type="button"
                            className="btn btn-primary btn-small"
                            disabled={working || !order.order_number || !hasCalled}
                            title={hasCalled ? undefined : 'Call the customer before confirming this order'}
                            onClick={() => handleCodAction(order.order_number, 'confirm')}
                          >
                            {working ? 'Working…' : 'Confirm'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger-outline btn-small"
                            disabled={working || !order.order_number || !hasCalled}
                            title={hasCalled ? undefined : 'Call the customer before cancelling this order'}
                            onClick={() => {
                              if (window.confirm(`Mark order ${order.order_number} as COD cancelled?`)) {
                                handleCodAction(order.order_number, 'cancel');
                              }
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                        {!hasCalled && (
                          <div className="call-feedback">Call the customer first</div>
                        )}
                        {rowAction.status === 'error' && (
                          <div className="call-feedback call-feedback-error">{rowAction.message}</div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={activeRows.length}
      />
    </>
  );
}

export default DailyOrders;
